/**
 * Scheduling (spec §19). Scheduled times are persisted in UTC and displayed in
 * the auction's timezone by the surface - never a timezone-naive timestamp
 * (spec §96). The entity carries the UTC instant AND the display timezone so a
 * surface renders "12:00 PM CDT" without guessing.
 *
 * The v1 recommendation is deterministic, not learned (spec §19: do not
 * overbuild). It anchors each channel to the sale's own instants - a lead time
 * before bidding opens for an announcement, near the close for a closing
 * campaign - by INSTANT arithmetic, so no timezone library is needed and the
 * result is stable and testable.
 */

import { z } from 'zod';
import { MarketingPlatformSchema } from './publish.js';
import type { CampaignObjective, MarketingChannel } from './catalog.js';

export const SCHEDULE_STATUSES = ['PENDING', 'SENT', 'CANCELLED', 'FAILED'] as const;
export const ScheduleStatusSchema = z.enum(SCHEDULE_STATUSES);
export type ScheduleStatus = z.infer<typeof ScheduleStatusSchema>;

export const MarketingScheduleSchema = z
  .object({
    id: z.string().min(1),
    assetId: z.string().min(1),
    platform: MarketingPlatformSchema,
    /** The instant to post, UTC. */
    scheduledAtUtc: z.string().datetime({ offset: true }),
    /** IANA timezone the surface renders it in (the auction's timezone). */
    displayTimezone: z.string().min(1),
    status: ScheduleStatusSchema,
    /** Set once the platform accepts a native schedule. */
    externalScheduleId: z.string().optional(),
  })
  .strict();
export type MarketingSchedule = z.infer<typeof MarketingScheduleSchema>;

// ---------------------------------------------------------------------------
// Deterministic v1 recommendation
// ---------------------------------------------------------------------------

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

export interface ScheduleRecommendationInput {
  objective: CampaignObjective;
  channels: readonly MarketingChannel[];
  /** The sale's own instants; either may be absent. */
  biddingOpensAt?: string;
  biddingClosesAt?: string;
  displayTimezone: string;
}

export interface RecommendedSlot {
  channel: MarketingChannel;
  atUtc: string;
}

/** Lead time before bidding opens for anticipation objectives; the anchor
 *  flips to the close for the closing/results objectives. */
function anchorAndOffset(objective: CampaignObjective): { anchor: 'OPEN' | 'CLOSE'; offsetMs: number } {
  switch (objective) {
    case 'AUCTION_ANNOUNCEMENT':
      return { anchor: 'OPEN', offsetMs: -6 * DAY };
    case 'CONSIGNMENTS_OPEN':
      return { anchor: 'OPEN', offsetMs: -14 * DAY };
    case 'BIDDING_OPEN':
      return { anchor: 'OPEN', offsetMs: 0 };
    case 'FEATURED_LOTS':
      return { anchor: 'OPEN', offsetMs: -3 * DAY };
    case 'CLOSING_TONIGHT':
      return { anchor: 'CLOSE', offsetMs: -8 * HOUR };
    case 'SALE_RESULTS':
      return { anchor: 'CLOSE', offsetMs: 2 * HOUR };
    case 'INDIVIDUAL_LOT':
    case 'CUSTOM':
      return { anchor: 'OPEN', offsetMs: -2 * DAY };
  }
}

/** Stagger channels through the anchor day so they do not all post at once. */
const CHANNEL_STAGGER_MS: Readonly<Record<MarketingChannel, number>> = {
  FACEBOOK_FEED: 0,
  INSTAGRAM_FEED: 1 * HOUR,
  REELS_TIKTOK: 3 * HOUR,
  YOUTUBE: 5 * HOUR,
  WEBSITE_HERO: -1 * HOUR,
  EMAIL_SMS: 2 * HOUR,
};

/**
 * A deterministic recommended slot per requested channel. Returns nothing for
 * a channel when the anchor instant it needs is absent (an announcement with
 * no open date has nothing to anchor to) - the surface then asks the tenant.
 */
export function recommendSchedule(input: ScheduleRecommendationInput): RecommendedSlot[] {
  const { anchor, offsetMs } = anchorAndOffset(input.objective);
  const base = anchor === 'OPEN' ? input.biddingOpensAt : input.biddingClosesAt;
  if (base === undefined) return [];
  const baseMs = Date.parse(base);
  if (Number.isNaN(baseMs)) return [];

  const out: RecommendedSlot[] = [];
  for (const channel of input.channels) {
    const atMs = baseMs + offsetMs + CHANNEL_STAGGER_MS[channel];
    out.push({ channel, atUtc: new Date(atMs).toISOString() });
  }
  return out;
}
